import { CheckmarkFilled, Error, Info } from '@1debit/icons';
import { type ComponentType } from 'react';
import {
	toast as hotToast,
	Toaster as ReactHotToaster,
	type ToasterProps,
	type Toast as ToastType,
} from 'react-hot-toast';

import { classNames } from '../../server/utils/classNames';

type ToastVariant = 'info' | 'error' | 'success' | 'plain';

export type ToastLayoutProps = ToastOptions & {
	message: string;
	variant: ToastVariant;
	instance: ToastType;
};

export type ToastCallToActionProps = {
	instanceId: string;
	className: string;
};

export type ToastOptions = {
	callToAction?: ComponentType<ToastCallToActionProps>;
};

function getIconVariant(variant: ToastVariant) {
	switch (variant) {
		case 'success':
			return <CheckmarkFilled data-testid="success-toast-icon" />;
		case 'error':
			return <Error data-testid="error-toast-icon" />;
		case 'info':
			return <Info data-testid="info-toast-icon" />;
		case 'plain':
		default:
			return null;
	}
}

export const ToastLayout = ({ message, variant, instance, callToAction: CallToAction }: ToastLayoutProps) => {
	const icon = getIconVariant(variant);

	const callToActionClasses = 'h-max p-1 text-label text-accent-green-400 cursor-pointer';

	return (
		<div
			data-id="toast"
			className={classNames(
				'flex max-w-layout-compact justify-between gap-4 rounded-lg bg-bg-raised-dark p-6 align-top text-content-light',
				{ 'animate-toast-show': instance.visible },
				{ 'animate-toast-hide': !instance.visible },
			)}
			role="status">
			{icon && <div className="h-max p-1">{icon}</div>}
			<p className="self-center break-words">{message}</p>
			{CallToAction ? (
				<CallToAction instanceId={instance.id} className={callToActionClasses} />
			) : (
				<button onClick={() => hotToast.dismiss(instance.id)} className={callToActionClasses}>
					Close
				</button>
			)}
		</div>
	);
};

const send = (message: string, variant: ToastVariant, options?: ToastOptions) =>
	hotToast.custom(instance => <ToastLayout message={message} instance={instance} variant={variant} {...options} />);

export const toast = {
	info: (message: string, options?: ToastOptions) => send(message, 'info', options),
	success: (message: string, options?: ToastOptions) => send(message, 'success', options),
	error: (message: string, options?: ToastOptions) => send(message, 'error', options),
	plain: (message: string, options?: ToastOptions) => send(message, 'plain', options),
	dismiss: hotToast.dismiss,
	remove: hotToast.remove,
};

export const Toaster = (props: ToasterProps) => {
	return (
		<ReactHotToaster
			position="bottom-center"
			toastOptions={{ duration: 4000 }}
			containerStyle={{ zIndex: 99999 }}
			{...props}
		/>
	);
};
